import React, { useRef, useState } from "react";
import { Card } from "./shared";
import { ROOM_GROUPS } from "./ShareRoomModal";

const LOCATIONS = ["DFW Metroplex", "North Texas", "Houston metro", "Southeast US"];

// Drop up to three wide photos on the cover — they cross-fade in the teaser. Read in the browser as
// data URLs, so they live with this package until you reload.
const MAX_COVERS = 3;

function CoverDrop({ value, onChange }) {
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const list = (value || "").split("|").filter(Boolean);
  const take = (files) => {
    const imgs = Array.from(files || []).filter((f) => f.type.startsWith("image/")).slice(0, MAX_MINUS(list.length));
    if (!imgs.length) return;
    Promise.all(imgs.map((f) => new Promise((res) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.readAsDataURL(f);
    }))).then((urls) => onChange([...list, ...urls].slice(0, MAX_COVERS).join("|")));
  };
  const drop = (i) => onChange(list.filter((_, n) => n !== i).join("|"));
  const full = list.length >= MAX_COVERS;
  return (
    <div>
      <div className={`cover-drop${over ? " over" : ""}${list.length ? " has" : ""}`} data-testid="pkg-coverdrop"
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}
        onClick={() => !full && input.current?.click()}>
        {list.length
          ? <div className="cover-strip" data-testid="pkg-coverstrip">
            {list.map((src, i) => (
              <div className="cover-thumb" key={i} data-testid={`pkg-coverthumb-${i}`}>
                <img src={src} alt="" />
                <button onClick={(e) => { e.stopPropagation(); drop(i); }} data-testid={`pkg-coverdrop-${i}`} title="Remove">×</button>
                <i>{i === 0 ? "first" : `fade ${i + 1}`}</i>
              </div>
            ))}
          </div>
          : <span>Drag up to {MAX_COVERS} wide photos here, or click to choose</span>}
        <input ref={input} type="file" accept="image/*" multiple hidden onChange={(e) => take(e.target.files)} data-testid="pkg-coverfile" />
      </div>
      <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
        <input className="inp" value="" onChange={(e) => e.target.value && onChange([...list, e.target.value].slice(0, MAX_COVERS).join("|"))}
          placeholder={full ? `${MAX_COVERS} photos added — remove one to add another` : "…or paste an image URL"}
          disabled={full} data-testid="pkg-coverphoto" />
        {list.length ? <button className="btn btn-sm" onClick={() => onChange("")} data-testid="pkg-coverclear">Clear all</button> : null}
      </div>
      {list.length > 1 ? <div className="ir-fine" style={{ marginTop: 4 }}>{list.length} photos cross-fade on the teaser cover. The first one is what prints to PDF.</div> : null}
    </div>
  );
}

const MAX_MINUS = (n) => Math.max(0, MAX_COVERS - n);

// Section picker + blind controls for the investor / lender package.
export function PackageConfig({ audience, secs, setSecs, sections, hideName, setHideName, hideAddr, setHideAddr,
  anonPipe, setAnonPipe, codeName, setCodeName, location, setLocation, address, prepFor, setPrepFor, prepEmail, setPrepEmail,
  coverPhoto, setCoverPhoto }) {
  const on = sections.filter((s) => secs[s.id]).length;
  const toggle = (id) => setSecs({ ...secs, [id]: !secs[id] });
  const setAll = (v) => setSecs(Object.fromEntries(sections.map((s) => [s.id, v])));

  return (
    <Card title={`What to share · ${on} of ${sections.length} sections`} testid="pkg-config" style={{ marginTop: 12 }}
      right={<span style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm" onClick={() => setAll(true)} data-testid="pkg-all">All</button>
        <button className="btn btn-sm" onClick={() => setAll(false)} data-testid="pkg-none">None</button>
      </span>}>
      <div className="grid g4">
        {ROOM_GROUPS.filter((g) => sections.some((s) => s.group === g)).map((g) => (
          <div key={g}>
            <div className="section-label">{g}</div>
            {sections.filter((s) => s.group === g).map((s) => (
              <label key={s.id} className="pkg-sec" data-testid={`pkg-sec-${s.id}`}>
                <input type="checkbox" checked={!!secs[s.id]} onChange={() => toggle(s.id)} data-testid={`pkg-check-${s.id}`} />
                <span><b>{s.label}</b><em>{s.desc}</em></span>
              </label>
            ))}
          </div>
        ))}
      </div>

      <div className="section-label" style={{ marginTop: 16 }}>Blind controls</div>
      <div className="grid g3">
        <div>
          <label className="pkg-sec" data-testid="pkg-hide-name">
            <input type="checkbox" checked={hideName} onChange={() => setHideName(!hideName)} data-testid="pkg-check-hidename" />
            <span><b>Hide the company name</b><em>every page shows your code name instead</em></span>
          </label>
          <input className="inp" value={codeName} onChange={(e) => setCodeName(e.target.value)} disabled={!hideName}
            placeholder="Project Summit" data-testid="pkg-codename" style={{ marginTop: 6 }} />
        </div>
        <div>
          <label className="pkg-sec" data-testid="pkg-hide-addr">
            <input type="checkbox" checked={hideAddr} onChange={() => setHideAddr(!hideAddr)} data-testid="pkg-check-hideaddr" />
            <span><b>Hide the address</b><em>{hideAddr ? "region only" : address || "full address shown"}</em></span>
          </label>
          <input className="inp" value={location} onChange={(e) => setLocation(e.target.value)} disabled={!hideAddr}
            list="pkg-location-presets" placeholder="DFW Metroplex" data-testid="pkg-location" style={{ marginTop: 6 }} />
          <datalist id="pkg-location-presets">{LOCATIONS.map((l) => <option key={l} value={l} />)}</datalist>
        </div>
        <div>
          <label className="pkg-sec" data-testid="pkg-anon-pipe">
            <input type="checkbox" checked={anonPipe} onChange={() => setAnonPipe(!anonPipe)} data-testid="pkg-check-anonpipe" />
            <span><b>Anonymise the add-on pipeline</b><em>targets show as "Target A, B, C" — same as the room</em></span>
          </label>
        </div>
      </div>

      <div className="section-label" style={{ marginTop: 16 }}>Prepared for</div>
      <div className="grid g3">
        <div className="field"><label>{audience === "lender" ? "Lender / officer name" : "Investor name"}</label>
          <input className="inp" value={prepFor} onChange={(e) => setPrepFor(e.target.value)} placeholder="Jane Investor" data-testid="pkg-prepfor" /></div>
        <div className="field"><label>Email</label>
          <input className="inp" type="email" value={prepEmail} onChange={(e) => setPrepEmail(e.target.value)} placeholder="jane@fund.com" data-testid="pkg-prepemail" /></div>
        <div className="field"><label>Cover photo</label>
          <CoverDrop value={coverPhoto} onChange={setCoverPhoto} /></div>
      </div>
    </Card>
  );
}
